import { useQuery, usePaginatedQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { useConvexMutation } from '../use-convex-mutation';

export interface SessionSummary {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  sessionType: string | null;
  courtNumber: number | null;
  coachUserId: string | null;
  coachEmail: string | null;
  attendeeCount: number;
  createdAt: string;
}

export interface CreateSessionInput {
  date: string;
  startTime: string;
  endTime: string;
  sessionType?: string;
  courtNumber?: number;
  coachUserId?: string;
  seriesId?: string;
}

type SessionType = "group" | "private" | "clinic" | "match_play" | "cardio_tennis" | "squads";

export function useDirectorSessions(initialNumItems = 50) {
  const { results, status, loadMore, isLoading } = usePaginatedQuery(
    api.director.sessions.list,
    {},
    { initialNumItems }
  );
  return {
    sessions: results,
    status,
    loadMore,
    isLoading,
  };
}

export function useDirectorSessionsForMonth(year: number, month: number) {
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const data = useQuery(api.director.sessions.listByDateRange, { startDate, endDate });
  return { data, isLoading: data === undefined, error: null };
}

export function useDirectorSessionDetail(sessionId: string | undefined) {
  const data = useQuery(
    api.director.sessions.getById,
    sessionId ? { sessionId: sessionId as Id<"trainingSessions"> } : "skip"
  );
  return { data, isLoading: data === undefined, error: null };
}

export function useCreateSession() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.sessions.create);
  return {
    isPending,
    mutateAsync: (args: CreateSessionInput) => mutateAsync({
      date: args.date,
      startTime: args.startTime,
      endTime: args.endTime,
      sessionType: args.sessionType as SessionType | undefined,
      courtNumber: args.courtNumber,
      coachUserId: args.coachUserId as Id<"users"> | undefined,
      seriesId: args.seriesId,
    }),
  };
}

export function useAddPlayersToSeries() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.sessions.addPlayersToSeries);
  return {
    isPending,
    mutateAsync: (args: { seriesId: string; playerIds: string[] }) =>
      mutateAsync({ seriesId: args.seriesId, playerIds: args.playerIds as Id<"players">[] }),
  };
}

export function useDeleteSession() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.sessions.remove);
  return {
    isPending,
    mutateAsync: (sessionId: string) =>
      mutateAsync({ sessionId: sessionId as Id<"trainingSessions"> }),
  };
}

export function useDeleteSessionSeries() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.sessions.removeSeries);
  return {
    isPending,
    mutateAsync: (sessionId: string, seriesId: string) =>
      mutateAsync({ sessionId: sessionId as Id<"trainingSessions">, seriesId }),
  };
}
